import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable, of, throwError, TimeoutError } from 'rxjs';
import { catchError, map, take, tap, timeout } from 'rxjs/operators';
import { Broadcaster } from '../events/broadcaster.class';
import { LogsService } from '../logs/logs.service';
import { NetworkService } from '../network/network.service';
import { LogoutService } from '../logout/logout.service';
import { AuthSignalService } from '../auth/auth-signal.service';
import { EnvironmentService } from '../environment/environment.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorService {

  public isOnline = this.networkService.hasInternetAccess;
  public unauthenticatedSignal = signal(false);
  public stateError: Boolean = false;
  private logger: Logger;

  constructor(
    private authSignalService: AuthSignalService,
    private broadcaster: Broadcaster,
    private loggingService: LoggingService,
    private logService: LogsService,
    private logoutService: LogoutService,
    private networkService: NetworkService,
    private httpClient: HttpClient,
    private environmentService: EnvironmentService,
  ) {
    this.logger = this.loggingService.getLogger("[ErrorService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  public handleHttpClientResponseError(
    httpErrorResponse: HttpErrorResponse | TimeoutError,
    requestMethod: string,
    calledFunction: string
  ): Observable<string> {

    // Log the error
    this.logService.logErrorData(httpErrorResponse, requestMethod, calledFunction);

    // Handle timeout errors first (check instance before accessing status)
    if (httpErrorResponse instanceof TimeoutError) {
      this.logger.warn('Request timeout from ' + calledFunction);
      this.showErrorModal(httpErrorResponse, requestMethod);
      return of('timeout');
    }

    // A value that is neither a TimeoutError nor an HttpErrorResponse only
    // reaches this handler because a client-side exception (e.g. a TypeError)
    // was thrown while a response was being processed inside an HTTP pipe's
    // catchError. That is an application bug, not a server failure - log it with
    // the real error object (so its stack survives) instead of letting it be
    // mislabelled as an HTTP "<requestMethod>" error in the dialog.
    if (!(httpErrorResponse instanceof HttpErrorResponse)) {
      this.logger.error(
        `Client-side error surfaced via HTTP error handler from ${calledFunction} (requestMethod=${requestMethod})`,
        httpErrorResponse
      );
      this.showErrorModal(httpErrorResponse, requestMethod, true);
      return of('clientError');
    }

    // Now safe to access status property (it's an HttpErrorResponse)
    const errorStatus = httpErrorResponse.status ?? null;

    // Handle authentication errors (401/403)
    if (errorStatus === 401 || errorStatus === 403) {
      this.logger.info('Authentication error detected - triggering sign out');
      return this.handleAuthenticationError();
    }

    // Handle all other HTTP errors
    this.showErrorModal(httpErrorResponse, requestMethod);
    return of('modal');
  }

  public showErrorModal(httpErrorResponse: HttpErrorResponse | any, requestMethod: string, isClientError: boolean = false) {
    this.broadcaster.broadcast('showErrorModal', ({
      httpErrorResponse: httpErrorResponse instanceof TimeoutError ? httpErrorResponse['name'] : httpErrorResponse,
      requestMethod,
      isClientError
    }));
  }

  private handleAuthenticationError(): Observable<string> {
    this.logger.info('Handling authentication error - signing out user');

    // signOutUser() now revokes tokens (deAuthenticateUser) and clears local
    // user/tenant/Pusher state in one chain, continuing even if revocation fails.
    return this.logoutService.signOutUser('session-ended').pipe(
      take(1),
      tap(() => {
        // Trigger the authenticated signal after successful sign out
        this.authSignalService.triggerUnauthenticated();
      }),
      map(() => 'unauthenticated'),
      catchError((error) => {
        this.logger.error('Error during sign out:', error);
        // Still trigger unauthenticated even if sign out fails
        this.authSignalService.triggerUnauthenticated();
        return of('unauthenticated');
      })
    );
  }


  // ######################################################################## //
  // ###  CHECK SERVER STATE - METHODS    ################################### //
  // ######################################################################## //

  public canConnectToServer(): Observable<boolean> {
    this.logger.info('canConnectToServer()');
    return this.checkServerHealth().pipe(
      map(() => {
        this.logger.info('Server health check successful');
        return true;
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.warn('Server health check failed:', error.status || 'unknown');
        return of(false);
      })
    );
  }

  public checkServerHealth(): Observable<any> {
    this.logger.info('checkServerHealth()');
    const checkServerHealthUrl: string = this.environmentService.getEnvironmentEndpoint['CHECK_SERVER_HEALTH'];

    if (!checkServerHealthUrl) {
      this.logger.error('checkServerHealth: No health check URL configured');
      return throwError(() => new Error('No health check URL configured'));
    }

    return this.httpClient.get<any>(checkServerHealthUrl).pipe(
      timeout(10000), // 10 second timeout
      tap(() => {
        this.logger.info('Server health check response received');
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('Server health check failed:', {
          status: error.status,
          statusText: error.statusText,
          url: checkServerHealthUrl
        });
        return throwError(() => error);
      })
    );
  }

  // ######################################################################## //
  // ###  OLD DEBUG QUERY METHODS    ######################################## //
  // ######################################################################## //

  // public delayServerResponse(INTERNAL_TEST_WAIT_TIMEUrl: string, timeToWait: number): Observable<any> { // GET a url from PrintJobsPage, a service cannot get a variable from another service
  //   this.logger.info('delayServerResponse()');
  //   return new Observable((observer) => {
  //     this.dialogService.showLoadingSpinnerDialog('delayServerResponse()').subscribe(() => {
  //       let waitSetTimeUrl: string = INTERNAL_TEST_WAIT_TIMEUrl + timeToWait;
  //       this.httpClient.post<any>(waitSetTimeUrl, {})
  //       .subscribe((response: any) => {
  //         this.dialogService.hideLoadingSpinnerDialog('delayServerResponse()');
  //         observer.next(response);
  //         observer.complete();
  //       }, (httpErrorResponse: HttpErrorResponse) => {
  //         this.dialogService.hideLoadingSpinnerDialog('ERROR delayServerResponse()');
  //         this.handleHttpClientResponseError(httpErrorResponse, 'POST', 'delayedServerResponse');
  //         observer.error(httpErrorResponse);
  //         observer.complete();
  //       });
  //     });
  //   });
  // }

  // /**
  //  * Test method to simulate HTTP errors with different status codes
  //  * Useful for testing error handling without actual server errors
  //  *
  //  * @param testEndpointUrl - Base URL for the test endpoint
  //  * @param statusCode - HTTP status code to simulate (404, 401, 500, etc.)
  //  * @returns Observable that emits the response or error
  //  */
  // public testHttpErrorStatus(testEndpointUrl: string, statusCode: number): Observable<any> {
  //   this.logger.info(`testHttpErrorStatus() - Testing status code: ${statusCode}`);

  //   const testUrl = `${testEndpointUrl}${statusCode}`;

  //   return this.httpClient.get<any>(testUrl).pipe(
  //     tap((response) => {
  //       this.logger.info(`Test request succeeded for status ${statusCode}:`, response);
  //     }),
  //     catchError((error: HttpErrorResponse) => {
  //       this.logger.error(`Test request failed with status ${statusCode}:`, error);

  //       // Automatically call the error handler to test the full flow
  //       this.handleHttpClientResponseError(error, 'GET', 'testHttpErrorStatus')
  //         .subscribe({
  //           next: (result) => {
  //             this.logger.info(`Error handling completed with result: ${result}`);
  //           },
  //           error: (handlingError) => {
  //             this.logger.error('Error in error handling:', handlingError);
  //           }
  //         });

  //       // Re-throw the error so the caller can also handle it if needed
  //       throw error;
  //     })
  //   );
  // }
}
